import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Download as DownloadIcon } from "lucide-react";
import { Logo } from "@/components/landing/Logo";
import { LanguageSelector } from "@/components/landing/LanguageSelector";
import { useLanguage } from "@/i18n/LanguageContext";

const SECTIONS = [
  { key: "features", href: "#features" },
  { key: "app", href: "#app-preview" },
  { key: "map", href: "#map" },
  { key: "business", href: "#for-business" },
  { key: "community", href: "#community" },
  { key: "hub", href: "#motohub", label: "MotoHUB" },
];

export const Navbar = () => {
  const { t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const bubbleBase =
    "pointer-events-auto rounded-2xl border backdrop-blur-xl shadow-[0_18px_60px_rgba(0,0,0,0.45)] transition-colors duration-300";
  const bubbleBg = scrolled
    ? "bg-black/60 border-white/14"
    : "bg-black/30 border-white/10";

  return (
    <div className="fixed top-3 sm:top-4 left-0 right-0 z-50 px-3 sm:px-4 pointer-events-none">
      <div className="max-w-6xl mx-auto flex items-center justify-center gap-2 sm:gap-3">
        {/* Logo bubble */}
        <motion.button
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          data-testid="navbar-logo"
          onClick={() => goTo("#hero")}
          aria-label="MotoGO home"
          className={`${bubbleBase} ${bubbleBg} shrink-0 inline-flex items-center justify-center p-1.5 sm:p-2 hover:border-white/20 hover:bg-black/45 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#39FF14]/70`}
        >
          <span className="absolute -inset-2 rounded-2xl bg-[#39FF14]/10 blur-xl pointer-events-none -z-10" aria-hidden="true" />
          <Logo className="h-9 w-9 sm:h-10 sm:w-10" />
        </motion.button>

        {/* Navbar pill */}
        <motion.nav
          data-testid="floating-navbar"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: 0.05 }}
          className={`${bubbleBase} ${bubbleBg} flex-1 min-w-0 max-w-4xl`}
        >
          <div className="flex items-center justify-between gap-2 px-2 sm:px-3 lg:px-4 py-2 sm:py-2.5 min-w-0">
            <div className="hidden lg:flex items-center gap-0.5 flex-1 justify-center min-w-0">
              {SECTIONS.map((l) => (
                <button
                  key={l.href}
                  onClick={() => goTo(l.href)}
                  data-testid={`navbar-link-${l.key}`}
                  className="relative px-3 py-2 text-sm text-white/75 hover:text-white transition-colors rounded-lg group whitespace-nowrap"
                >
                  <span>{l.label || t.nav[l.key]}</span>
                  <span className="absolute left-3 right-3 -bottom-0.5 h-px bg-[#39FF14] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300" />
                </button>
              ))}
            </div>

            <div className="lg:hidden flex-1" />

            <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
              <LanguageSelector compact />
              <button
                data-testid="navbar-download-button"
                onClick={() => goTo("#download")}
                className="hidden sm:inline-flex items-center gap-1.5 sm:gap-2 rounded-xl bg-[#39FF14] text-black font-semibold px-3 lg:px-4 py-2 text-xs sm:text-sm hover:bg-[#6BFF55] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#39FF14]/70 active:translate-y-[1px] whitespace-nowrap"
              >
                <DownloadIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="hidden md:inline">{t.nav.getTheApp}</span>
                <span className="md:hidden">{t.nav.getApp}</span>
              </button>
              <button
                data-testid="navbar-menu-button"
                onClick={() => setOpen((o) => !o)}
                className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-xl border border-white/10 bg-white/5 text-white hover:bg-white/10 transition-colors"
                aria-label={open ? t.nav.closeMenu : t.nav.openMenu}
                aria-expanded={open}
              >
                {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <AnimatePresence>
            {open && (
              <motion.div
                key="mobile-menu"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                className="lg:hidden overflow-hidden border-t border-white/8"
              >
                <div className="px-3 sm:px-4 py-3 flex flex-col gap-1">
                  {SECTIONS.map((l) => (
                    <button
                      key={l.href}
                      onClick={() => goTo(l.href)}
                      data-testid={`mobile-link-${l.key}`}
                      className="text-left px-3 py-3 text-base text-white/85 hover:text-white hover:bg-white/5 rounded-xl transition-colors"
                    >
                      {l.label || t.nav[l.key]}
                    </button>
                  ))}
                  <button
                    data-testid="mobile-download-button"
                    onClick={() => goTo("#download")}
                    className="mt-2 inline-flex items-center justify-center gap-2 rounded-xl bg-[#39FF14] text-black font-semibold px-4 py-3 text-sm hover:bg-[#6BFF55] transition-colors"
                  >
                    <DownloadIcon className="w-4 h-4" />
                    {t.nav.getTheApp}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.nav>
      </div>
    </div>
  );
};
